import { Component } from '@theme/component';

/**
 * The localStorage key holding the shopper's wishlist product handles.
 */
const STORAGE_KEY = 'wishlist:handles';

/**
 * The event name dispatched on `document` whenever the wishlist changes.
 */
const WISHLIST_UPDATE_EVENT = 'wishlist:update';

/**
 * The vibration pattern played when the heart is tapped, if enabled and supported.
 */
const VIBRATION_PATTERN = 10;

/**
 * The placeholder in the wishlist section's fetch URL template that stands for the product handle.
 */
const HANDLE_PLACEHOLDER = '__HANDLE__';

/**
 * An event signaling that the wishlist changed.
 * @extends {Event}
 */
class WishlistUpdateEvent extends Event {
  /**
   * @param {string[]} handles - The wishlist product handles after the change.
   */
  constructor(handles) {
    super(WISHLIST_UPDATE_EVENT, { bubbles: true });
    this.detail = { handles };
  }
}

/**
 * Reads and writes the wishlist in localStorage and broadcasts updates.
 */
const WishlistStore = {
  /**
   * @returns {string[]} The stored product handles.
   */
  getHandles() {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
      return Array.isArray(parsed) ? parsed.filter((handle) => typeof handle === 'string') : [];
    } catch {
      return [];
    }
  },

  /**
   * @param {string} handle - The product handle.
   * @returns {boolean} Whether the product is in the wishlist.
   */
  has(handle) {
    return this.getHandles().includes(handle);
  },

  /**
   * Removes a product and broadcasts the change.
   * @param {string} handle - The product handle.
   */
  remove(handle) {
    const handles = this.getHandles();
    const index = handles.indexOf(handle);
    if (index === -1) return;

    handles.splice(index, 1);

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    } catch {
      // Storage can be unavailable (for example in private browsing); the change still applies to this page view.
    }

    document.dispatchEvent(new WishlistUpdateEvent(handles));
  },

  /**
   * Adds or removes a product and broadcasts the change.
   * @param {string} handle - The product handle.
   * @returns {boolean} Whether the product was added.
   */
  toggle(handle) {
    const handles = this.getHandles();
    const index = handles.indexOf(handle);

    if (index === -1) {
      handles.unshift(handle);
    } else {
      handles.splice(index, 1);
    }

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    } catch {
      // Storage can be unavailable (for example in private browsing); the change still applies to this page view.
    }

    document.dispatchEvent(new WishlistUpdateEvent(handles));

    return index === -1;
  },
};

/**
 * A heart button that adds or removes a product from the wishlist.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} button - The heart button.
 * @property {HTMLElement} status - The live region announcing wishlist changes.
 *
 * @extends {Component<Refs>}
 */
class WishlistHeart extends Component {
  requiredRefs = ['button', 'status'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(WISHLIST_UPDATE_EVENT, this.onWishlistUpdate);
    this.sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(WISHLIST_UPDATE_EVENT, this.onWishlistUpdate);
  }

  onWishlistUpdate = () => {
    this.sync();
  };

  /**
   * Syncs the pressed state and label with the stored wishlist.
   */
  sync() {
    if (this.dataset.action === 'remove') {
      this.refs.button.removeAttribute('aria-pressed');
      this.refs.button.setAttribute('aria-label', this.dataset.labelRemove ?? '');
      return;
    }

    const inWishlist = WishlistStore.has(this.dataset.productHandle ?? '');

    this.classList.toggle('wishlist-heart--active', inWishlist);
    this.refs.button.setAttribute('aria-pressed', String(inWishlist));
    this.refs.button.setAttribute(
      'aria-label',
      inWishlist ? (this.dataset.labelRemove ?? '') : (this.dataset.labelAdd ?? '')
    );
  }

  /**
   * Removes or toggles the product in the wishlist, announces the change, and vibrates if enabled.
   */
  handleToggle() {
    const handle = this.dataset.productHandle;
    if (!handle) return;

    let added = false;

    if (this.dataset.action === 'remove') {
      WishlistStore.remove(handle);
    } else {
      added = WishlistStore.toggle(handle);
    }

    this.refs.status.textContent = added ? (this.dataset.statusAdded ?? '') : (this.dataset.statusRemoved ?? '');

    if (this.dataset.vibrate === 'true' && typeof navigator.vibrate === 'function') {
      navigator.vibrate(VIBRATION_PATTERN);
    }
  }
}

/**
 * The wishlist page: renders a table row for each saved product.
 *
 * @typedef {object} PageRefs
 * @property {HTMLElement} table - The table wrapper holding the header and the product rows.
 * @property {HTMLElement} grid - The table body the product rows render into.
 * @property {HTMLElement} emptyState - The empty state shown when the wishlist has no products.
 * @property {HTMLElement} loading - The loading indicator shown while rows are fetched.
 * @property {HTMLElement} error - The message shown when saved products could not be loaded.
 *
 * @extends {Component<PageRefs>}
 */
class WishlistPage extends Component {
  requiredRefs = ['table', 'grid', 'emptyState', 'loading', 'error'];

  /**
   * Cached section responses by product handle.
   * @type {Map<string, string>}
   */
  #cardCache = new Map();

  /**
   * The abort controller for the active render, if there is one.
   * @type {AbortController | null}
   */
  #activeRender = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(WISHLIST_UPDATE_EVENT, this.onWishlistUpdate);
    this.render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(WISHLIST_UPDATE_EVENT, this.onWishlistUpdate);
    this.#activeRender?.abort();
    this.#activeRender = null;
  }

  onWishlistUpdate = () => {
    this.render();
  };

  /**
   * Re-renders the grid from the stored wishlist.
   */
  async render() {
    const handles = WishlistStore.getHandles();

    this.#activeRender?.abort();
    const controller = new AbortController();
    this.#activeRender = controller;

    this.refs.emptyState.hidden = handles.length > 0;
    this.refs.loading.hidden = handles.length === 0;
    this.refs.table.hidden = handles.length === 0;
    this.refs.error.hidden = true;

    if (handles.length === 0) {
      this.refs.grid.replaceChildren();
      this.refs.loading.hidden = true;
      return;
    }

    let failed = 0;
    const cards = [];

    try {
      for (const handle of handles) {
        const card = await this.#fetchCard(handle, controller.signal);
        if (controller.signal.aborted) return;

        if (card) {
          cards.push(card);
        } else {
          failed += 1;
        }
      }
    } catch (error) {
      // An aborted render means a newer render took over.
      if (error instanceof DOMException && error.name === 'AbortError') return;
      throw error;
    }

    this.refs.grid.replaceChildren(...cards);
    this.refs.loading.hidden = true;
    this.refs.error.hidden = failed === 0;
  }

  /**
   * Fetches the rendered wishlist card for a product.
   * @param {string} handle - The product handle.
   * @param {AbortSignal} signal - The signal aborting an outdated render.
   * @returns {Promise<DocumentFragment | null>} The card fragment, or null when it could not be loaded.
   */
  async #fetchCard(handle, signal) {
    const cached = this.#cardCache.get(handle);
    if (cached) return this.#createCardFragment(cached);

    const template = this.dataset.fetchUrlTemplate;
    if (!template) return null;

    try {
      const url = new URL(template.replace(HANDLE_PLACEHOLDER, encodeURIComponent(handle)), window.location.origin);

      // Carry the current query parameters (preview theme, localization) so the section
      // renders with the same theme and context as the page the shopper is viewing.
      for (const [key, value] of new URLSearchParams(window.location.search)) {
        url.searchParams.set(key, value);
      }
      url.searchParams.set('section_id', 'wishlist-card');

      const response = await fetch(url, { signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      this.#cardCache.set(handle, html);

      return this.#createCardFragment(html);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      console.warn(`[wishlist] Could not load the card for product "${handle}":`, error);
      return null;
    }
  }

  /**
   * Extracts the product row from a rendered section response and injects its stylesheet.
   * @param {string} html - The section rendering response.
   * @returns {DocumentFragment | null} The row fragment, or null when the response holds no row.
   */
  #createCardFragment(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const item = parsed.querySelector('.wishlist-row');
    if (!item) return null;

    const fragment = document.createDocumentFragment();
    fragment.appendChild(document.importNode(item, true));

    const stylesheet = parsed.querySelector('style[data-section-stylesheet]');
    if (stylesheet) {
      const existing = this.querySelector('style[data-section-stylesheet]');
      if (existing) {
        existing.textContent = stylesheet.textContent;
      } else {
        this.prepend(document.importNode(stylesheet, true));
      }
    }

    return fragment;
  }
}

if (!customElements.get('wishlist-heart')) {
  customElements.define('wishlist-heart', WishlistHeart);
}

if (!customElements.get('wishlist-page')) {
  customElements.define('wishlist-page', WishlistPage);
}
